import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  GET_ACCOUNT_DETAILS,
  ADD_USER_TO_ACCOUNT,
  REMOVE_USER_FROM_ACCOUNT,
  UPDATE_USER_ROLE,
  GET_USER_PROFILE,
} from '../../graphql';
import { GetAccountDetailsResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { useTinycmsPlans } from '../account/tinycmsPlans';
import { SiteHomeNotice } from '../cms/SiteHomeNotice';
import { teamSeats } from './teamSeats';
import { useNavigate } from 'react-router-dom';
import { lastActiveLabel } from './accountAgents';
import { showInkToast } from '../cms/InkToast';
import {
  TRANSFER_SITE_OWNERSHIP,
  billingFollowsOwner,
  billingWarnings,
  useBillingContact,
} from './siteOwner';
import {
  END_SUPPORT_ACCESS,
  SupportRequests,
  shortDate,
  supportInitials,
  supportName,
  useSupportAccess,
} from './supportAccess';

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

type UserRole = 'admin' | 'member' | 'campaign';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  member: 'Member',
  campaign: 'Campaign',
};

/** Team order after you and the owner. Technical support (no seat) comes last. */
const ROLE_ORDER = ['admin', 'member', 'campaign', 'support'];

// The dated campaigns someone with the Campaign role could be put on (invite).
const TEAM_CAMPAIGNS = gql`
  query TeamCampaigns($accountId: ID!) {
    campaignList(accountId: $accountId)
  }
`;

export function TeamSettings() {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('member');
  // Campaign role: the campaigns they'll work on.
  const [newUserCampaigns, setNewUserCampaigns] = useState<string[]>([]);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState<boolean>(false);
  const [userToRemove, setUserToRemove] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [userToChangeRole, setUserToChangeRole] = useState<{
    id: string;
    email: string;
    currentRole: string;
    newRole: UserRole;
  } | null>(null);
  const [isAdminConfirmOpen, setIsAdminConfirmOpen] = useState<boolean>(false);
  // Make owner (hand the site's billing to another admin).
  const [transferTo, setTransferTo] = useState<{ userId: string; email: string } | null>(null);
  const [moveBilling, setMoveBilling] = useState(true);
  const navigate = useNavigate();

  const { accountId, selectedAccount, isLoading: accountsLoading, refetchAccounts } = useAccount();
  const { plans: tinycmsPlans } = useTinycmsPlans();
  const { contact, refetch: refetchContact } = useBillingContact(accountId);
  const [transferOwnership, { loading: isTransferring }] = useMutation(TRANSFER_SITE_OWNERSHIP);
  // TinyCMS technical support: requests the owner answers, and current access.
  const { view: support, refetch: refetchSupport } = useSupportAccess(accountId);
  const [endSupport] = useMutation(END_SUPPORT_ACCESS);
  const { data: campData } = useQuery<{
    campaignList: Array<{ id: string; name: string; status: string; deadline?: string | null }>;
  }>(TEAM_CAMPAIGNS, {
    variables: { accountId },
    skip: !accountId || !openDialogNeedsCampaigns(),
    errorPolicy: 'all',
  });
  function openDialogNeedsCampaigns() {
    return newUserRole === 'campaign';
  }
  const pickableCampaigns = (campData?.campaignList || []).filter(
    (c) =>
      c.status !== 'completed' &&
      (!c.deadline || new Date(c.deadline).getTime() + 86400000 >= Date.now())
  );

  const {
    data: accountData,
    loading,
    error,
    refetch,
  } = useQuery<GetAccountDetailsResponse>(GET_ACCOUNT_DETAILS, {
    variables: { id: accountId },
    skip: !accountId,
    onCompleted: (data) => {
      console.log('Account Query Completed:', data);
    },
    onError: (error) => {
      console.error('Account Query Error:', error);
    },
  });

  const [addUserToAccount] = useMutation(ADD_USER_TO_ACCOUNT, {
    onCompleted: () => {
      handleCloseDialog();
      refetch();
      refetchAccounts();
    },
    // Shown, not just logged: the server's refusal says why ("The Personal plan
    // includes up to 1 team member…").
    onError: (error) => {
      showInkToast(error.message || 'That didn’t work. Try again.');
    },
  });

  const [removeUserFromAccount, { loading: isRemovingUser }] = useMutation(
    REMOVE_USER_FROM_ACCOUNT,
    {
      onCompleted: async () => {
        setIsRemoveConfirmOpen(false);
        setUserToRemove(null);
        refetch();
        refetchAccounts();
        // Billing mail or the card still theirs? Say so (the owner is emailed too).
        const { data } = await refetchContact();
        const [warning] = billingWarnings(data?.billingContact ?? null);
        if (warning) {
          showInkToast(warning, {
            label: 'Review billing',
            onClick: () => navigate('/settings/billing'),
          });
        }
      },
      onError: (error) => {
        showInkToast(error.message || 'That didn’t work. Try again.');
      },
    }
  );

  const [updateUserRole, { loading: isUpdatingRole }] = useMutation(UPDATE_USER_ROLE, {
    onCompleted: () => {
      setIsAdminConfirmOpen(false);
      setUserToChangeRole(null);
      refetch();
      refetchAccounts();
    },
    onError: (error) => {
      showInkToast(error.message || 'That didn’t work. Try again.');
    },
  });

  // Get current user's email to prevent self-removal
  const { data: userProfileData } = useQuery<UserProfileData>(GET_USER_PROFILE);
  const currentUserEmail = userProfileData?.me?.email;

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewUserEmail('');
    setNewUserRole('member');
    setNewUserCampaigns([]);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUserEmail('');
    setNewUserRole('member');
    setNewUserCampaigns([]);
  };

  const handleInviteUser = async () => {
    if (!newUserEmail.trim() || !accountId) return;

    try {
      await addUserToAccount({
        variables: {
          input: {
            accountId: accountId,
            userId: newUserEmail,
            role: newUserRole,
            ...(newUserRole === 'campaign' ? { campaignIds: newUserCampaigns } : {}),
          },
        },
      });
      if (newUserRole === 'campaign') {
        showInkToast(
          `Invite sent to ${newUserEmail.trim()}. They’ll see only the campaigns picked.`
        );
      }
    } catch (error) {
      showInkToast(error instanceof Error ? error.message : 'That didn’t work. Try again.');
    }
  };

  // The page's own shape in grey bars while it loads (QA round 7: a lone
  // loader read as an empty page for seconds).
  if (accountsLoading || loading) return <TeamSkeleton />;
  if (error) return <p className="ink-lede">Could not load the team.</p>;

  // You first, then the owner, then by role (Prototype: "Hai Tran · you" leads).
  const teamMembers = [...(accountData?.account?.users || [])].sort((a, b) => {
    const rank = (m: typeof a) =>
      m.user.email === currentUserEmail
        ? 0
        : contact?.ownerUserId && contact.ownerUserId === m.user.id
          ? 1
          : 2 + Math.max(0, ROLE_ORDER.indexOf(m.role));
    return rank(a) - rank(b) || a.user.email.localeCompare(b.user.email);
  });
  const pausedMembers = accountData?.account?.pausedMembers || [];
  // Technical support uses no seat.
  const memberCount = teamMembers.filter((m) => m.role !== 'support').length;

  // Get current plan from account data
  const currentPlan = accountData?.account?.plan || selectedAccount?.plan || '';

  const handleRemoveMember = (userAccount: { id: string; user: { email: string } }) => {
    setUserToRemove({
      id: userAccount.id,
      email: userAccount.user.email,
    });
    setIsRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!accountId || !userToRemove) {
      console.error('Account ID and user to remove are required');
      return;
    }

    try {
      await removeUserFromAccount({
        variables: {
          accountId: accountId,
          id: userToRemove.id,
        },
      });
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };

  /**
   * The role menu's Admin / Member (Prototype, QA round 13: one click, not a
   * dialog). Admin → Member still asks first: it takes team settings away.
   */
  const changeRoleTo = (
    userAccount: { id: string; user: { email: string }; role: string },
    newRole: UserRole
  ) => {
    if (!accountId || userAccount.role === newRole) return;
    if (userAccount.role === 'admin') {
      setUserToChangeRole({
        id: userAccount.id,
        email: userAccount.user.email,
        currentRole: userAccount.role,
        newRole,
      });
      setIsAdminConfirmOpen(true);
      return;
    }
    void updateUserRole({ variables: { accountId, id: userAccount.id, role: newRole } }).catch(
      () => {
        // onError shows the message
      }
    );
  };

  const executeRoleUpdate = async () => {
    if (!accountId || !userToChangeRole) {
      return;
    }

    try {
      await updateUserRole({
        variables: {
          accountId: accountId,
          id: userToChangeRole.id,
          role: userToChangeRole.newRole,
        },
      });
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  const handleConfirmAdminChange = async () => {
    setIsAdminConfirmOpen(false);
    await executeRoleUpdate();
  };

  // A tinycms plan says how many members it includes; older plans keep their caps.
  const tinycmsPlan = tinycmsPlans.find((p) => p.key === String(currentPlan || '').toLowerCase());
  const seatCaps: Record<string, number> = { Enterprise: 10, Pro: 3, Basic: 1 };
  const seatCap = tinycmsPlan?.maxMembers ?? (seatCaps[currentPlan] || 10);
  // Seats as the server counts them, and what to say when they're all taken.
  const youOwn = !contact?.ownerUserId || contact.ownerUserId === userProfileData?.me?.id;
  const seats = teamSeats({
    members: memberCount,
    paused: pausedMembers.length,
    cap: seatCap,
    planName: tinycmsPlan?.label || currentPlan || null,
    youOwn,
    ownerEmail: contact?.ownerEmail || null,
  });
  const seatsUsed = seats.used;
  const seatsFull = seats.full;

  return (
    <div>
      <h1 className="ink-h1">Team</h1>
      <p className="ink-lede">The people who work on this account and what each may do.</p>
      {accountId && support ? (
        <SupportRequests
          accountId={accountId}
          view={support}
          onChanged={() => {
            refetchSupport();
            refetch();
          }}
        />
      ) : null}

      {/* Above People, so it's read before the Invite button it explains. */}
      {seatsFull ? (
        <div className="ink-team-full">
          <SiteHomeNotice
            text={seats.note || ''}
            action={youOwn ? { label: 'Upgrade plan →', to: '/settings/billing' } : null}
          />
        </div>
      ) : null}
      <div className="ink-conn-head ink-acct-first">
        <h2>
          People{' '}
          <span className="ink-conn-head-muted">
            {seatsUsed} of {seatCap} {seatCap === 1 ? 'seat' : 'seats'}
          </span>
        </h2>
        <Dialog
          open={openDialog && !seatsFull}
          onOpenChange={(open) => {
            if (open && !seatsFull) handleOpenDialog();
            else handleCloseDialog();
          }}
        >
          <DialogTrigger asChild>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={seatsFull}
              title={seatsFull ? 'No seats left on your plan' : undefined}
            >
              Invite a person
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to add a new member to your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="Email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={newUserRole}
                  onValueChange={(value) => setNewUserRole(value as UserRole)}
                >
                  <SelectTrigger id="invite-role">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="campaign">Campaign · only their campaigns</SelectItem>
                  </SelectContent>
                </Select>
                <p className="ink-muted" style={{ fontSize: 13 }}>
                  {newUserRole === 'campaign'
                    ? 'Sees only the campaigns they’re on, and can edit everything in them: the landing page, articles and posts. Nothing goes live until you approve it.'
                    : 'Admins can manage team settings and members'}
                </p>
              </div>
              {newUserRole === 'campaign' ? (
                <div className="space-y-2">
                  <Label>Campaigns</Label>
                  {pickableCampaigns.length ? (
                    <div className="tm-camps">
                      {pickableCampaigns.map((c) => (
                        <label key={c.id} className="ink-team-check">
                          <input
                            type="checkbox"
                            checked={newUserCampaigns.includes(c.id)}
                            onChange={(e) =>
                              setNewUserCampaigns((list) =>
                                e.target.checked
                                  ? [...list, c.id]
                                  : list.filter((id) => id !== c.id)
                              )
                            }
                          />
                          {c.name}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="ink-muted" style={{ fontSize: 13 }}>
                      No campaigns running or coming up. Start one in Campaigns first.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleInviteUser}
                disabled={
                  !newUserEmail.trim() || (newUserRole === 'campaign' && !newUserCampaigns.length)
                }
                className="bg-button-primary text-button-primary"
              >
                <Mail className="size-4 mr-2" />
                Send Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="ink-team ink-acct-card">
        <div className="ink-team-row ink-team-head" aria-hidden="true">
          <span />
          <span>Member</span>
          <span>Role</span>
          <span>Last active</span>
        </div>
        {teamMembers.length === 0 ? (
          <p className="ink-lede" style={{ marginTop: 16 }}>
            No one on the team yet.
          </p>
        ) : (
          teamMembers.map((userAccount) => {
            if (userAccount.role === 'support') {
              const grant = support?.active.find(
                (g) => g.requesterEmail.toLowerCase() === userAccount.user.email.toLowerCase()
              );
              const ends = shortDate(grant?.expiresAt);
              const isMe = userAccount.user.email === currentUserEmail;
              const name = grant ? supportName(grant) : userAccount.user.email.split('@')[0];
              return (
                <div className="ink-team-row" key={userAccount.user.id}>
                  <span className="ink-team-avatar" data-tone="soft">
                    {supportInitials(name)}
                  </span>
                  <div>
                    <div className="ink-team-name">
                      {name} <span className="ink-team-tag">tinycms support</span>
                    </div>
                    <div className="ink-team-email">{userAccount.user.email}</div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="ink-team-role">
                        Technical support ▾
                        {ends ? (
                          <span className="ink-team-email" style={{ display: 'block' }}>
                            Access ends {ends}
                          </span>
                        ) : null}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-core-dark">
                      <DropdownMenuItem disabled>
                        {grant?.ticket ? `Ticket ${grant.ticket}. ` : ''}Can do what an admin can,
                        except billing and the team.{ends ? ` Access ends ${ends}.` : ''}
                      </DropdownMenuItem>
                      {grant && (support?.canDecide || isMe) ? (
                        <DropdownMenuItem
                          style={{ color: 'var(--ink-red)' }}
                          onClick={async () => {
                            if (!accountId) return;
                            try {
                              await endSupport({ variables: { accountId, id: grant.id } });
                              showInkToast(
                                isMe
                                  ? 'You left this site.'
                                  : `${name} from tinycms support no longer has access.`
                              );
                              refetchSupport();
                              refetch();
                              refetchAccounts();
                            } catch (err) {
                              showInkToast(
                                err instanceof Error ? err.message : 'That didn’t work. Try again.'
                              );
                            }
                          }}
                        >
                          {isMe ? 'Leave this site' : 'End access now'}
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <span className="ink-team-since">
                    {lastActiveLabel(
                      (userAccount.user as { lastSeenAt?: string | null }).lastSeenAt,
                      userAccount.createdAt
                    )}
                  </span>
                </div>
              );
            }
            const roleKey: UserRole =
              userAccount.role === 'admin'
                ? 'admin'
                : userAccount.role === 'campaign'
                  ? 'campaign'
                  : 'member';
            const role = ROLE_LABEL[roleKey];
            // Campaign role: what they see (Prototype: "Only sees Fall price guide").
            const campaignsLine =
              roleKey === 'campaign'
                ? userAccount.campaigns?.length
                  ? `Only sees ${userAccount.campaigns.join(', ')}`
                  : 'On no campaigns yet'
                : null;
            const name = userAccount.user.email.split('@')[0];
            const lastActive = lastActiveLabel(
              (userAccount.user as { lastSeenAt?: string | null }).lastSeenAt,
              userAccount.createdAt
            );
            const isCurrentUser = userAccount.user.email === currentUserEmail;
            const isOwner = Boolean(
              contact?.ownerUserId && contact.ownerUserId === userAccount.user.id
            );
            // The owner stays until they hand the site to another admin.
            const canRemoveMember = !isCurrentUser && memberCount > 1 && !isOwner;
            const canMakeOwner =
              Boolean(contact?.canManage) && !isOwner && userAccount.role === 'admin';
            const initials = name.slice(0, 2).toUpperCase();

            return (
              <div className="ink-team-row" key={userAccount.user.id}>
                <span className="ink-team-avatar">{initials}</span>
                <div>
                  <div className="ink-team-name">
                    {name}
                    {isCurrentUser ? <span className="ink-team-you"> · you</span> : null}
                  </div>
                  <div className="ink-team-email">{userAccount.user.email}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="ink-team-role">
                      {isOwner ? 'Owner' : role} ▾
                      {isOwner ? (
                        <span className="ink-team-email ink-team-role-sub">
                          Admin · manages billing
                        </span>
                      ) : campaignsLine ? (
                        <span className="ink-team-email" style={{ display: 'block' }}>
                          {campaignsLine}
                        </span>
                      ) : null}
                    </button>
                  </DropdownMenuTrigger>
                  {/* Prototype: "Change role" with Admin / Member as choices, then Make
                      owner, then Remove in red below a line. A 264px card, so the
                      owner's note wraps instead of running off the table. */}
                  <DropdownMenuContent
                    align="start"
                    className="bg-core-dark ink-team-menu"
                    style={{ width: 264 }}
                  >
                    {userAccount.id && !isOwner ? (
                      <>
                        <DropdownMenuLabel className="ink-team-menu-label">
                          Change role
                        </DropdownMenuLabel>
                        {(
                          [
                            ['admin', 'Admin'],
                            ['member', 'Member'],
                            ['campaign', 'Campaign · only their campaigns'],
                          ] as Array<[UserRole, string]>
                        )
                          // You don't take yourself down to the Campaign role.
                          .filter(([key]) => key !== 'campaign' || !isCurrentUser)
                          .map(([key, label]) => (
                            <DropdownMenuItem
                              key={key}
                              disabled={isUpdatingRole}
                              onClick={() => changeRoleTo(userAccount, key)}
                            >
                              <span style={{ flex: 1 }}>{label}</span>
                              {roleKey === key ? <span aria-label="current role">✓</span> : null}
                            </DropdownMenuItem>
                          ))}
                      </>
                    ) : null}
                    {canMakeOwner ? <DropdownMenuSeparator /> : null}
                    {canMakeOwner ? (
                      <DropdownMenuItem
                        onClick={() => {
                          setMoveBilling(true);
                          setTransferTo({
                            userId: userAccount.user.id,
                            email: userAccount.user.email,
                          });
                        }}
                      >
                        Make owner
                      </DropdownMenuItem>
                    ) : null}
                    {isOwner ? (
                      <DropdownMenuItem disabled className="ink-team-menu-note">
                        {contact?.isOwner
                          ? 'You’re the owner. Make another admin owner to step down.'
                          : 'The owner manages billing'}
                      </DropdownMenuItem>
                    ) : null}
                    {canRemoveMember && userAccount.id ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => handleRemoveMember(userAccount)}
                        >
                          Remove
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="ink-team-since">{lastActive}</span>
              </div>
            );
          })
        )}
        {pausedMembers.map((m) => {
          const name = m.email.split('@')[0];
          return (
            <div className="ink-team-row" key={m.id} data-paused="true">
              <span className="ink-team-avatar">{name.slice(0, 2).toUpperCase()}</span>
              <div>
                <div className="ink-team-name">{name}</div>
                <div className="ink-team-email">{m.email}</div>
              </div>
              <span className="ink-team-paused">Paused · not on your plan</span>
              <button
                type="button"
                className="ink-bill-link"
                data-muted="true"
                onClick={() => handleRemoveMember({ id: m.id, user: { email: m.email } })}
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>
      {pausedMembers.length ? (
        <p className="ink-bill-note" style={{ marginTop: 12 }}>
          Your plan includes {seatCap} team member{seatCap === 1 ? '' : 's'}, so{' '}
          {pausedMembers.length === 1 ? 'one person is' : `${pausedMembers.length} people are`}{' '}
          paused: they can’t open this site until you upgrade in Plan &amp; billing, and then
          they’re back with the same role. Nothing was removed.
        </p>
      ) : null}

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.email}</strong> from your team?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRemoveConfirmOpen(false);
                setUserToRemove(null);
              }}
              disabled={isRemovingUser}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemove} disabled={isRemovingUser}>
              {isRemovingUser ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Make owner */}
      <Dialog open={Boolean(transferTo)} onOpenChange={(open) => !open && setTransferTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make {transferTo?.email} the owner?</DialogTitle>
            <DialogDescription>
              The owner manages billing: the plan, the card and the billing email. You stay an
              admin. Only the new owner can hand it back.
            </DialogDescription>
          </DialogHeader>
          {billingFollowsOwner(contact) ? (
            <label className="ink-team-check">
              <input
                type="checkbox"
                checked={moveBilling}
                onChange={(e) => setMoveBilling(e.target.checked)}
              />
              Send billing emails to {transferTo?.email} too
            </label>
          ) : (
            <p className="ink-team-email">
              Billing emails keep going to {contact?.effectiveBillingEmail}.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTo(null)} disabled={isTransferring}>
              Cancel
            </Button>
            <Button
              disabled={isTransferring}
              className="bg-button-primary text-button-primary"
              onClick={async () => {
                if (!accountId || !transferTo) return;
                try {
                  await transferOwnership({
                    variables: {
                      accountId,
                      userId: transferTo.userId,
                      moveBillingEmail: moveBilling,
                    },
                  });
                  showInkToast(`${transferTo.email} is the owner now.`);
                  setTransferTo(null);
                  await refetchContact();
                } catch (err) {
                  showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
                }
              }}
            >
              {isTransferring ? 'Handing over…' : 'Make owner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Change Confirmation Dialog */}
      <Dialog open={isAdminConfirmOpen} onOpenChange={setIsAdminConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Role Change</DialogTitle>
            <DialogDescription>
              You are about to change <strong>{userToChangeRole?.email}</strong> from{' '}
              <strong>Admin</strong> to{' '}
              <strong>{ROLE_LABEL[userToChangeRole?.newRole || 'member']}</strong>. This will remove
              their ability to manage team settings and members
              {userToChangeRole?.newRole === 'campaign'
                ? ', and they’ll see only the campaigns they’re on'
                : ''}
              . Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAdminConfirmOpen(false);
              }}
              disabled={isUpdatingRole}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmAdminChange}
              disabled={isUpdatingRole}
            >
              {isUpdatingRole ? 'Updating...' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Team (title, line, member table) in grey bars while it loads. */
function TeamSkeleton() {
  return (
    <div role="status" aria-label="Loading the team" aria-busy="true">
      <h1 className="ink-h1" style={{ margin: 0 }}>
        Team
      </h1>
      <span
        className="ink-skel-bar"
        aria-hidden="true"
        style={{ display: 'block', width: 360, maxWidth: '80%', margin: '14px 0 4px' }}
      />
      <div className="ink-team" aria-hidden="true">
        <div className="ink-team-row ink-team-head">
          <span />
          <span>Member</span>
          <span>Role</span>
          <span>Last active</span>
        </div>
        {[140, 110, 160].map((w, i) => (
          <div className="ink-team-row" key={i}>
            <span className="ink-team-avatar ink-skel-bar" style={{ height: 36 }} />
            <span style={{ display: 'grid', gap: 6 }}>
              <span className="ink-skel-bar" data-size="lg" style={{ width: w }} />
              <span className="ink-skel-bar" style={{ width: w + 40 }} />
            </span>
            <span className="ink-skel-bar" style={{ width: 56 }} />
            <span className="ink-skel-bar" style={{ width: 70 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
